import { impersonationStore, tokenStore } from '@/lib/api'

/**
 * POST a JSON body and read a text/event-stream reply frame by frame, for
 * MPG AI (ported from Printscape's lib/sse.js).
 *
 * axios cannot hand over a body as it arrives and EventSource cannot send an
 * Authorization header, so this goes through fetch() and repeats the two
 * things the axios instance in lib/api does: the bearer token, and the 401
 * handling. Keep it in step with lib/api if either changes.
 *
 * A plain JSON reply (AI_STREAM_ENABLED off on the server) is turned into the
 * same events, so the page has one code path.
 */
const BASE = import.meta.env.VITE_API_URL ?? '/api/v1'

export type StreamEvent = 'start' | 'tool' | 'reason' | 'delta' | 'proposal' | 'done' | 'error'

export class StreamError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

function handleUnauthorized() {
  if (impersonationStore.get()) {
    const returnTo = impersonationStore.returnTo()
    impersonationStore.clear()
    window.location.assign(returnTo)
    return
  }
  tokenStore.clear()
  window.location.assign('/login')
}

export async function streamPost(
  path: string,
  body: unknown,
  { onEvent, signal }: { onEvent: (event: StreamEvent, data: Record<string, unknown>) => void; signal?: AbortSignal },
): Promise<void> {
  const token = tokenStore.get()
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream, application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    if (res.status === 401) handleUnauthorized()
    const payload = await res.json().catch(() => null)
    const first = payload?.errors ? (Object.values(payload.errors)[0] as string[] | undefined)?.[0] : undefined
    throw new StreamError(first ?? payload?.message ?? `Request failed (${res.status})`, res.status, payload?.code)
  }

  if (!res.headers.get('content-type')?.includes('text/event-stream')) {
    const json = await res.json()
    const data = json.data ?? {}
    if (data.message?.content) onEvent('delta', { text: data.message.content })
    onEvent('done', { message_id: data.message?.id, title: data.conversation?.title, proposals: data.proposals ?? [] })
    return
  }

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // Frames end with a blank line; one starting with ':' is a keepalive.
    let cut: number
    while ((cut = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, cut)
      buffer = buffer.slice(cut + 2)
      if (!frame || frame.startsWith(':')) continue

      const name = (frame.match(/^event: (.*)$/m)?.[1] ?? 'delta') as StreamEvent
      const raw = frame.match(/^data: (.*)$/m)?.[1]
      if (!raw) continue

      try {
        onEvent(name, JSON.parse(raw))
      } catch {
        // One malformed frame is not worth ending the answer over.
      }
    }
  }
}
